import { atom } from 'jotai';
import type { DashboardFilterOperator, DashboardQueryFilter } from './dashboardFilters';
import { toQueryFilter } from './dashboardFilters';
import type { DashboardCalculatedField } from './dashboardCalculations';
import { CALC_METRIC_AGGREGATION, calcFieldOf, isCalcField } from './dashboardCalculations';
import type { DashboardGridItem, LegacyLayoutRow } from './dashboardGridLayout';
import { DASHBOARD_GRID_COLS, normalizeGrid, rowsToGrid } from './dashboardGridLayout';
import type { DashboardRollingPeriod } from './dashboardRollingDates';
import type { DashboardStatComparison } from './dashboardStat';
import { LAST_YEAR } from './dashboardStat';
import { isRollingPeriod, rollingQueryFilters } from './dashboardRollingDates';

// ---- Dynamic reporting field metadata (POST /api/v1/reporting/dynamic) ----
export type DashboardFieldType = 'string' | 'double' | 'float' | 'int';

export const DASHBOARD_ALL_FIELDS: { field: string; type: DashboardFieldType }[] = [
    { field: 'id', type: 'string' },
    { field: 'name', type: 'string' },
    { field: 'reference', type: 'string' },
    { field: 'buyer', type: 'string' },
    { field: 'agency', type: 'string' },
    { field: 'client', type: 'string' },
    { field: 'scheduled', type: 'string' },
    { field: 'frame_id', type: 'string' },
    { field: 'inside_campaign_settings_date', type: 'string' },
    { field: 'playout_start_date', type: 'string' },
    { field: 'playout_start_time', type: 'string' },
    { field: 'playout_start_datetime', type: 'string' },
    { field: 'playout_end_date', type: 'string' },
    { field: 'playout_end_time', type: 'string' },
    { field: 'playout_end_datetime', type: 'string' },
    { field: 'playout_day_of_week', type: 'string' },
    { field: 'creative_id', type: 'string' },
    { field: 'creative_name', type: 'string' },
    { field: 'sum_of_play', type: 'double' },
    { field: 'expected_plays', type: 'double' },
    { field: 'sum_of_creatives', type: 'double' },
    { field: 'sum_of_time', type: 'double' },
    { field: 'sov', type: 'double' },
    { field: 'slot_duration', type: 'double' },
    { field: 'expected_time', type: 'double' },
    { field: 'audience_sov', type: 'double' },
    { field: 'audience', type: 'double' },
    { field: 'playout_percentage_plays', type: 'double' },
    { field: 'playout_percentage_time', type: 'double' },
    { field: 'expected_audience', type: 'double' },
    { field: 'audience_received', type: 'double' },
    { field: 'audience_received_percent', type: 'double' },
    { field: 'expected_impacts_in_schedule', type: 'double' },
    { field: 'impacts_received_in_schedule', type: 'double' },
    { field: 'impacts_received_out_of_schedule', type: 'double' },
    { field: 'impacts_received_unbooked', type: 'double' },
    { field: 'expected_plays_in_schedule', type: 'double' },
    { field: 'plays_received_in_schedule', type: 'double' },
    { field: 'plays_received_out_of_schedule', type: 'double' },
    { field: 'plays_received_unbooked', type: 'double' },
    { field: 'expected_time_in_schedule', type: 'double' },
    { field: 'time_received_in_schedule', type: 'double' },
    { field: 'time_received_out_of_schedule', type: 'double' },
    { field: 'time_received_unbooked', type: 'double' },
    { field: 'day', type: 'string' },
    { field: 'hour', type: 'string' },
    { field: 'display_id', type: 'string' },
    { field: 'issue_id', type: 'string' },
    { field: 'downtime', type: 'string' },
    { field: 'plays_with_verification', type: 'double' },
    { field: 'time_with_verification', type: 'double' },
    { field: 'impacts_with_verification', type: 'double' },
    { field: 'energy_uses_kwh', type: 'double' },
    { field: 'carbon_emission_kgs_co2e', type: 'double' },
    { field: 'display_sov', type: 'double' },
    { field: 'display_slots', type: 'double' },
    { field: 'display_slot_duration', type: 'float' },
    { field: 'display_town', type: 'string' },
    { field: 'display_tv_region', type: 'string' },
    { field: 'display_conurbation', type: 'string' },
    { field: 'display_type', type: 'string' },
    { field: 'display_resolution', type: 'string' },
    { field: 'display_filetypes', type: 'string' },
    { field: 'display_env', type: 'string' },
    { field: 'display_surface_area', type: 'string' },
    { field: 'display_lat', type: 'string' },
    { field: 'display_long', type: 'string' },
    { field: 'display_format', type: 'string' },
    { field: 'display_line_item_id', type: 'string' },
    { field: 'display_line_item_name', type: 'string' },
    { field: 'display_owner', type: 'string' },
    { field: 'display_owner_id', type: 'string' },
    { field: 'display_screen_name_summary', type: 'string' },
    { field: 'playout_start_timestamp', type: 'int' },
    { field: 'playout_start_hour', type: 'int' },
    { field: 'playout_end_timestamp', type: 'int' },
    { field: 'playout_end_hour', type: 'int' },
    { field: 'environment', type: 'string' },
    { field: 'campaign_id', type: 'string' },
];

const NUMERIC_TYPES: DashboardFieldType[] = ['double', 'float', 'int'];
export const DASHBOARD_METRIC_FIELDS = DASHBOARD_ALL_FIELDS.filter(f =>
    NUMERIC_TYPES.includes(f.type),
).map(f => f.field);
export const DASHBOARD_DIMENSION_FIELDS = DASHBOARD_ALL_FIELDS.map(f => f.field);
const DASHBOARD_ALL_FIELD_NAMES = DASHBOARD_ALL_FIELDS.map(f => f.field);

export const DASHBOARD_AGGREGATIONS = ['sum', 'avg', 'min', 'max', 'count'] as const;
export type DashboardAggregation = (typeof DASHBOARD_AGGREGATIONS)[number];

export const DASHBOARD_GRANULARITIES = [
    'hour',
    'day',
    'week',
    'biweek',
    'month',
    'quarter',
    'year',
] as const;
export type DashboardGranularity = (typeof DASHBOARD_GRANULARITIES)[number];

export const DASHBOARD_GRAPH_TYPES = [
    'bar',
    'horizontalBar',
    'rankedBar',
    'deliveryComparison',
    'line',
    'pie',
    'donut',
    'table',
    'stat',
] as const;
export type DashboardGraphType = (typeof DASHBOARD_GRAPH_TYPES)[number];

// Widgets always fetch the maximum number of rows; the visible categories are capped
// client-side to the widget's Top N, so there is no user-facing "limit" control.
export const DASHBOARD_MAX_LIMIT = 100000;

// How many dimensions each graph type can meaningfully use. Tables show a column
// per dimension (unlimited); bar/horizontalBar/line support a 2nd dimension as a
// series split; the rest are single-dimension.
export const dashboardMaxDimensions = (graphType: DashboardGraphType): number => {
    if (graphType === 'table') return Infinity;
    if (
        graphType === 'bar' ||
        graphType === 'horizontalBar' ||
        graphType === 'rankedBar' ||
        graphType === 'line'
    ) {
        return 2;
    }
    return 1;
};

// Friendly names for the graph-type picker. The two horizontal styles differ by whether
// values sit on an axis (horizontalBar) or are printed at the end of each bar (rankedBar).
export const DASHBOARD_GRAPH_TYPE_LABELS: Record<DashboardGraphType, string> = {
    bar: 'Bar (vertical)',
    horizontalBar: 'Horizontal bar (with axis)',
    rankedBar: 'Ranked bar (labels on bars, no axis)',
    deliveryComparison: 'Expected vs Total (stacked)',
    line: 'Line',
    pie: 'Pie',
    donut: 'Donut',
    table: 'Table',
    stat: 'Single value',
};

// ---- Single-value (stat) widgets ----
// How a single figure is dressed: an icon beside its heading, the size of the figure, and its
// style — the plain number, or a ring filling towards 100% (for a ratio such as pacing).
export const DASHBOARD_STAT_ICONS = [
    'none',
    'campaign',
    'target',
    'visibility',
    'trendingUp',
    'trendingDown',
    'speed',
    'play',
    'screen',
    'people',
    'money',
    'calendar',
    'check',
    'warning',
] as const;
export type DashboardStatIcon = (typeof DASHBOARD_STAT_ICONS)[number];
export const DASHBOARD_STAT_SIZES = ['small', 'medium', 'large'] as const;
export type DashboardStatSize = (typeof DASHBOARD_STAT_SIZES)[number];
export const DASHBOARD_STAT_STYLES = ['number', 'ring'] as const;
export type DashboardStatStyle = (typeof DASHBOARD_STAT_STYLES)[number];

// The skin's brand palette colours a widget can be tinted with (6 per skin).
export const DASHBOARD_COLORS = [
    'primary',
    'secondary',
    'tertiary',
    'quaternary',
    'quinary',
    'senary',
] as const;
export type DashboardColor = (typeof DASHBOARD_COLORS)[number];

// Ranking basis for the categories, independent of what the bars display.
//   value      – highest raw/actual value
//   percentage – actual ÷ expected × 100
//   difference – actual − expected
//   name       – alphabetical
export const DASHBOARD_SORT_BASES = ['value', 'percentage', 'difference', 'name'] as const;
export type DashboardSortBasis = (typeof DASHBOARD_SORT_BASES)[number];

// Show the top N categories, or the bottom N.
export const DASHBOARD_RANK_FROM = ['top', 'bottom'] as const;
export type DashboardRankFrom = (typeof DASHBOARD_RANK_FROM)[number];

// Which series a comparison chart shows.
export const DASHBOARD_COMPARISON_SERIES = ['both', 'expected', 'actual'] as const;
export type DashboardComparisonSeries = (typeof DASHBOARD_COMPARISON_SERIES)[number];

// Which series a line widget's live toolbar shows. Mirrors the summary reporting control;
// "delivered" is everything that is not an expected series.
export const DASHBOARD_VIEW_FILTERS = ['both', 'expected', 'delivered'] as const;
export type DashboardViewFilter = (typeof DASHBOARD_VIEW_FILTERS)[number];

// ---- Widget config ----
export type DashboardMetric = { field: string; aggregation: DashboardAggregation };
export type DashboardDimension = { field: string; granularity?: DashboardGranularity };
export type DashboardFilter = {
    field: string;
    operator: DashboardFilterOperator;
    value: string | number | string[];
    // Global filters only: the source this filter runs against — 'default' for the built-in
    // dynamic report, otherwise a datasource id. It reaches exactly the widgets querying that
    // source. Sources don't share a schema (`buyer` vs `campaign_buyer`), so a filter is only
    // ever meaningful against the one it was built on. Absent means 'default'.
    source?: string;
    // A date window that moves with the calendar ("last 7 days"). When set, `operator` and
    // `value` are ignored and the period is turned into dates each time the query is sent.
    rolling?: DashboardRollingPeriod;
};

export const DASHBOARD_FILTER_SOURCE_DEFAULT = 'default';

// A widget's source in the vocabulary global filter scopes use.
export const dashboardWidgetSource = (datasourceId?: string | number): string =>
    datasourceId != null ? String(datasourceId) : DASHBOARD_FILTER_SOURCE_DEFAULT;

// The global filters that apply to one widget: those built against the source it queries.
export const globalFiltersForWidget = (
    filters: DashboardFilter[],
    datasourceId?: string | number,
): DashboardFilter[] => {
    const source = dashboardWidgetSource(datasourceId);
    return filters.filter(f => (f.source ?? DASHBOARD_FILTER_SOURCE_DEFAULT) === source);
};

// The year-to-date window, campaigns inside their settings date, and programmatic references
// excluded. These used to be a separate, always-on "base filters" layer; they are now simply
// the default dashboard's global filters on the default report, so what the Global filters
// tab lists is everything that actually applies.
export const getDashboardBaseFilters = (): DashboardFilter[] => {
    const startOfYear = new Date();
    startOfYear.setMonth(0, 1);
    startOfYear.setHours(0, 0, 0, 0);
    return [
        { field: 'playout_start_date', operator: 'gte', value: startOfYear.toISOString() },
        { field: 'inside_campaign_settings_date', operator: 'eq', value: 1 },
        { field: 'reference', operator: 'not_like', value: '%Programmatic%' },
    ];
};

// ---- The summary boxes that used to sit above every dashboard ----
// Before version 4 a dashboard had a fixed row of boxes along the top, set up on a tab of their
// own. They are stat widgets now, like any other; these are only what the default dashboard and
// normalizeDashboardConfig need to build them. See summaryBoxWidgets below.
const SUMMARY_BOXES = ['campaigns', 'target', 'total', 'additional', 'pacing'] as const;
type SummaryBox = (typeof SUMMARY_BOXES)[number];
type SummaryRole = 'campaign' | 'expected' | 'inSchedule' | 'outOfSchedule' | 'unbooked';

// What a stored summary held. Read once, by normalizeDashboardConfig, and turned into widgets.
type LegacySummaryConfig = {
    source?: string;
    // Which of the source's columns each figure came from; '' left a figure out.
    fields?: Partial<Record<SummaryRole, string>>;
    filters?: DashboardFilter[];
    // Show the "+x% vs last year" line under each box.
    comparison?: boolean;
    boxes?: string[];
};

const DEFAULT_SUMMARY_FIELDS: Record<SummaryRole, string> = {
    campaign: 'campaign_id',
    expected: 'expected_impacts_in_schedule',
    inSchedule: 'impacts_received_in_schedule',
    outOfSchedule: 'impacts_received_out_of_schedule',
    unbooked: 'impacts_received_unbooked',
};

// Scope is a client-side concept; the API only ever sees field/operator/value, and only the
// operators it knows (toQueryFilter). A rolling period becomes the two date bounds it covers
// today.
export const toQueryFilters = (
    filters: DashboardFilter[],
    now: Date = new Date(),
): DashboardQueryFilter[] =>
    filters.flatMap(filter =>
        isRollingPeriod(filter.rolling)
            ? rollingQueryFilters(filter.field, filter.rolling, now)
            : toQueryFilter(filter),
    );
export type DashboardSort = { field: string; direction: 'asc' | 'desc' };

// Built-in (fixed) charts that reproduce the original dashboard. When set, the widget
// renders a dedicated chart and ignores the generic query fields.
export type DashboardBuiltinKey =
    | 'impactDelivery'
    | 'ownersDonut'
    | 'ownersImpacts'
    | 'ownersAdditional'
    | 'ownersPerf'
    | 'envsPerf';

export type DashboardWidgetConfig = {
    id: string;
    title: string;
    // Leave the dashboard's global filters off this widget, so it answers its question against the unfiltered source.
    ignoreGlobalFilters?: boolean;
    // A name typed by the user, shown instead of the one inferred from the query. Kept apart
    // from `title` (which the built-in widgets use for their own fixed English headings) so
    // an existing dashboard doesn't suddenly start showing those instead of translated ones.
    customTitle?: string;
    graphType: DashboardGraphType;
    // When set, this widget queries the given saved datasource (Athena table) via
    // POST /reporting/datasource/:id/query, and its field pickers come from that
    // datasource's /fields. When unset, it uses the generic /reporting/dynamic report.
    datasourceId?: string | number;
    metrics: DashboardMetric[];
    dimensions: DashboardDimension[];
    filters: DashboardFilter[];
    sort: DashboardSort[];
    limit: number;
    // Stack the metrics on top of each other (layered) instead of grouping them
    // side-by-side. Only applies to bar / horizontalBar with multiple raw metrics.
    stacked?: boolean;
    // How many categories to show before rolling the rest into "Others".
    topN?: number;
    // Roll everything past the top/bottom N into a single "Others" entry. Off by default:
    // a chart headed "Top 5" is about those five, and the extra bar or slice mostly dwarfs
    // them. Turn it on where the remainder is worth seeing.
    showOthers?: boolean;
    // Pie and donut only: plot every category instead of just the ranked N, so the circle
    // really is all of the data.
    showAllSlices?: boolean;
    // Pie and donut only: label each slice with its share of what is plotted, so the parts
    // add up to 100%.
    percentOfTotal?: boolean;
    // Which end of the ranking those are taken from: the highest (default) or the lowest.
    rankFrom?: DashboardRankFrom;
    // Single colour applied to all bars/line/points (theme palette key).
    color?: DashboardColor;
    // Ranking basis for the categories.
    sortBy?: DashboardSortBasis;
    // Show Expected vs Actual per category (like the reporting summary tab).
    comparison?: boolean;
    comparisonSeries?: DashboardComparisonSeries;
    expectedColor?: DashboardColor;
    actualColor?: DashboardColor;
    // Ranked bar only: draw expected and actual as two bars side by side rather than one bar
    // that runs to the larger of the two.
    comparisonSideBySide?: boolean;
    builtin?: DashboardBuiltinKey;
    // Starting position of the live chart toolbar (line widgets). The toolbar is shown only
    // when the widget is full width or expanded, and overrides these for the session only —
    // they are the saved defaults, not the current state.
    cumulative?: boolean;
    showNumbers?: boolean;
    showPercent?: boolean;
    viewFilter?: DashboardViewFilter;
    // Tables only: rows per page (they show every row, a page at a time), and whether a totals
    // row closes the table.
    pageSize?: number;
    showTotals?: boolean;
    // Single value only. The first metric is the figure; any others are listed under it.
    statIcon?: DashboardStatIcon;
    // Unset is large, the size single values have always been.
    statSize?: DashboardStatSize;
    statStyle?: DashboardStatStyle;
    // Green when the figure is above zero (or, as a ring, above 100%), red when below.
    signColors?: boolean;
    // A line under the figure with its change on another period: the one before, the same one
    // a set time earlier, or set dates. Every date filter reaching the widget moves to it.
    compareTo?: DashboardStatComparison;
};

export const DASHBOARD_PAGE_SIZES = [10, 25, 50, 100] as const;
export const DASHBOARD_DEFAULT_PAGE_SIZE = 10;

export type DashboardChartControls = {
    cumulative: boolean;
    showNumbers: boolean;
    showPercent: boolean;
    viewFilter: DashboardViewFilter;
};

// ---- What a widget measures its delivery against ----
// Normally an `expected_*` metric. A Geopath audience column plays the same part — the audited
// figure delivery is read against — so it drives the Expected/Delivered filter, percent and
// comparison the same way. It is the headline figure there, though, so it takes the primary
// colour and the delivered series steps back to the secondary one.
export const isAudienceField = (field: string): boolean => /geopath_audience/i.test(field);
export const isExpectedField = (field: string): boolean =>
    /^expected/i.test(field) || isAudienceField(field);

// The metric a widget compares against: a true expected one first, else an audience one.
export const expectedMetricOf = <M extends { field: string }>(metrics: M[]): M | undefined =>
    metrics.find(m => /^expected/i.test(m.field)) ?? metrics.find(m => isAudienceField(m.field));

// The colours a comparison chart starts with when the widget doesn't set its own.
export const comparisonColorDefaults = (
    widget: Pick<DashboardWidgetConfig, 'metrics' | 'color'>,
): { expected: DashboardColor; actual: DashboardColor } =>
    isAudienceField(expectedMetricOf(widget.metrics)?.field ?? '')
        ? { expected: 'primary', actual: 'secondary' }
        : { expected: 'secondary', actual: widget.color ?? 'primary' };

// Seed the live toolbar from the widget's saved defaults. Comparison mode already has its own
// "Show" dropdown, so that wins when it applies rather than adding a second source of truth.
export const seedChartControls = (widget: DashboardWidgetConfig): DashboardChartControls => ({
    cumulative: widget.cumulative === true,
    showNumbers: widget.showNumbers === true,
    showPercent: widget.showPercent === true,
    viewFilter:
        widget.comparison && widget.comparisonSeries
            ? widget.comparisonSeries === 'actual'
                ? 'delivered'
                : widget.comparisonSeries
            : widget.viewFilter ?? 'both',
});

const KNOWN_DIM_TITLE_FIELDS = new Set([
    'display_owner',
    'display_env',
    'campaign_id',
    'display_format',
    'display_town',
    'display_tv_region',
    'display_type',
    'display_conurbation',
]);

// Turn an underscored field name into a human label. Prefer a translation
// (Pages.DashboardHome.fields.<field>); fall back to a prettified form.
const prettifyField = (field: string): string =>
    field
        .replace(/_/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase())
        .trim();

export const humanizeField = (
    field: string,
    t: (key: string, opts?: Record<string, unknown>) => string,
): string => {
    if (!field) return '';
    const key = `Pages.DashboardHome.fields.${field}`;
    const translated = t(key);
    if (!translated || translated === key || translated.includes('DashboardHome.fields')) {
        return prettifyField(field);
    }
    return translated;
};

// A "trend" widget plots a continuous series rather than a ranking: a single-dimension line
// chart, or any single-dimension widget bucketed by time. The ranking machinery — top N, the
// "Others" bucket, sorting by value — is wrong for these, because the axis IS the dimension and
// every point on it matters. Two-dimension widgets are excluded: there the cap limits how many
// series are drawn, which is still meaningful.
export const isTrendWidget = (widget: DashboardWidgetConfig): boolean => {
    if (widget.dimensions?.length !== 1) return false;
    if (widget.dimensions[0]?.granularity != null) return true;
    return widget.graphType === 'line';
};

// Build a widget's title from its config (translated), so titles stay in sync with what
// the chart shows and don't need to be typed manually.
export const inferWidgetTitle = (
    widget: DashboardWidgetConfig,
    t: (key: string, opts?: Record<string, unknown>) => string,
    // The full breakdown shows every entry, so drop the "Top N" prefix there.
    omitTopN = false,
    // The dashboard's calculated fields, so a calculated dimension is named as its author named it.
    calcs: DashboardCalculatedField[] = [],
): string => {
    const ns = 'Pages.DashboardHome.widgetTitle';
    // A trend shows every point, so "Top 5" would be a lie on the heading.
    const dropTopN = omitTopN || isTrendWidget(widget);
    if (widget.graphType === 'deliveryComparison') return t(`${ns}.impactsDelivery`);

    const nameOf = (field: string): string =>
        isCalcField(field)
            ? calcs.find(c => calcFieldOf(c.id) === field)?.name ?? ''
            : KNOWN_DIM_TITLE_FIELDS.has(field)
            ? t(`${ns}.dim_${field}`)
            : field
                  .replace(/^display_/, '')
                  .replace(/_/g, ' ')
                  .replace(/\b\w/g, c => c.toUpperCase())
                  .trim();

    const dim = widget.dimensions[0]?.field ?? '';
    const dimName = nameOf(dim);
    // A 2nd dimension splits each category into a series — reflect that in the title.
    const dim2 = widget.dimensions[1]?.field;
    const bySuffix = dim2 ? ` ${t(`${ns}.by`)} ${nameOf(dim2)}` : '';

    const topN = widget.topN != null && widget.topN > 0 ? Math.floor(widget.topN) : 5;
    const rankWord = t(`${ns}.${widget.rankFrom === 'bottom' ? 'bottom' : 'top'}`);
    const base = dropTopN ? `${dimName}${bySuffix}` : `${rankWord} ${topN} ${dimName}${bySuffix}`;

    if (widget.comparison) {
        const audited = isAudienceField(expectedMetricOf(widget.metrics)?.field ?? '');
        return `${base} (${t(`${ns}.${audited ? 'auditedVsActual' : 'expectedVsActual'}`)})`;
    }

    // A chart of one calculated measure says which, as the built-in "(All Impacts)" charts do.
    const [only, ...more] = widget.metrics;
    const calcName =
        only && more.length === 0 && isCalcField(only.field)
            ? calcs.find(c => calcFieldOf(c.id) === only.field)?.name
            : undefined;
    return calcName ? `${base} (${translateWidgetTitle(calcName, t)})` : base;
};

// Words a custom name is likely to contain that the app has its own wording for: the ones
// that move with the locale — an "impact" here is an "impression" in the US, a "media owner"
// is a "partner", a "town" is a CBSA. Each term lists the wording from every market, so a
// name can be typed in either dialect (or a mix of both) and still follows the viewer's
// locale. Terms whose wording is more than one word in some market (TV Region / State,
// Conurbation / Market (DMA)) only match where they're a single word.
const TITLE_VOCABULARY_WORDS: Record<string, string[]> = {
    'CommonWords.Impacts': ['impacts', 'impressions'],
    'CommonWords.Pack': ['pack', 'package'],
    'CommonWords.Frame': ['frame', 'unit'],
    'CommonWords.Town': ['town', 'cbsa'],
    'CommonWords.Towns': ['towns', 'cbsas'],
    'CommonWords.Conurbation': ['conurbation'],
    'CommonWords.Conurbations': ['conurbations'],
    'CommonWords.Region': ['region', 'state'],
    'CommonWords.Owner': ['owner', 'partner'],
    'CommonWords.Owners': ['owners', 'partners'],
    'Pages.DashboardHome.widgetTitle.top': ['top'],
    'Pages.DashboardHome.widgetTitle.bottom': ['bottom'],
    'Pages.DashboardHome.widgetTitle.by': ['by'],
};

// Terms whose wording is more than one word in at least one market. These have to be matched
// before the single words above, or only part of the term is translated and the rest is left
// stranded in front of it — "Media Owners" becoming "Media Partners" rather than "Partners".
// Each lists every market's wording, so a name round-trips: type either dialect, get the
// viewer's.
const TITLE_VOCABULARY_PHRASES: Record<string, string[]> = {
    'CommonWords.MediaOwners': ['media owners', 'partners'],
    'CommonWords.MediaOwner': ['media owner', 'partner'],
    'CommonWords.TVRegions': ['tv regions'],
    'CommonWords.TVRegion': ['tv region'],
};

const TITLE_PHRASES: Record<string, string> = Object.entries(TITLE_VOCABULARY_PHRASES).reduce(
    (acc, [key, phrases]) => {
        phrases.forEach(phrase => {
            acc[phrase] = key;
        });
        return acc;
    },
    {} as Record<string, string>,
);

// One pass over the name, phrases first: regex alternation is ordered, and the longest
// phrases are listed first, so "media owners" wins over "media owner" and both win over the
// bare word pattern that follows.
const TITLE_TOKEN_PATTERN = [
    ...Object.keys(TITLE_PHRASES)
        .sort((a, b) => b.length - a.length)
        .map(phrase => phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')),
    '[A-Za-z][A-Za-z0-9_]*',
].join('|');

const TITLE_VOCABULARY: Record<string, string> = Object.entries(TITLE_VOCABULARY_WORDS).reduce(
    (acc, [key, words]) => {
        words.forEach(word => {
            acc[word] = key;
        });
        return acc;
    },
    {} as Record<string, string>,
);

// Give a translation the casing of the word it replaces, so "owners" stays lower case
// mid-sentence and "Owners" stays capitalised at the start of one. Only the first letter is
// touched: some of the wording is an acronym (CBSA, DMA), and upper-casing a whole
// replacement because the word it replaced was an acronym would shout.
const matchCase = (typed: string, translated: string): string => {
    if (!translated) return translated;
    return typed[0] === typed[0].toUpperCase()
        ? translated.charAt(0).toUpperCase() + translated.slice(1)
        : translated.charAt(0).toLowerCase() + translated.slice(1);
};

// Translate the parts of a custom widget name we have words for, and leave the rest exactly
// as typed. An underscored token is read as a field name (`display_owner` becomes the field's
// label, translated where one exists and prettified where it doesn't); a plain word is looked
// up in the vocabulary above. Anything else — a brand, a client name, a number — is untouched.
export const translateWidgetTitle = (
    title: string,
    t: (key: string, opts?: Record<string, unknown>) => string,
): string =>
    title.replace(new RegExp(TITLE_TOKEN_PATTERN, 'gi'), token => {
        const lower = token.toLowerCase();
        // A phrase is translated whole, so the words it is made of are never looked up
        // individually and cannot be half-replaced.
        const phraseKey = TITLE_PHRASES[lower];
        if (phraseKey) {
            const phrase = t(phraseKey);
            return !phrase || phrase === phraseKey ? token : matchCase(token, phrase);
        }
        if (token.includes('_')) return humanizeField(lower, t);
        const key = TITLE_VOCABULARY[lower];
        if (!key) return token;
        const translated = t(key);
        return !translated || translated === key ? token : matchCase(token, translated);
    });

// What a widget is actually called on screen: the user's own name when they've given one,
// otherwise the name inferred from its query.
export const resolveWidgetTitle = (
    widget: DashboardWidgetConfig,
    t: (key: string, opts?: Record<string, unknown>) => string,
    omitTopN = false,
    calcs: DashboardCalculatedField[] = [],
): string => {
    const custom = widget.customTitle?.trim();
    return custom ? translateWidgetTitle(custom, t) : inferWidgetTitle(widget, t, omitTopN, calcs);
};

export type DashboardLayoutConfig = {
    // Each widget's box on the grid. See dashboardGridLayout.ts.
    items: DashboardGridItem[];
    // Before version 3: rows split by percentage. Read once by normalizeDashboardConfig and
    // turned into boxes.
    rows?: LegacyLayoutRow[];
};

// Bumped when the stored shape changes in a way normalizeDashboardConfig has to migrate.
//   2 – base filters folded into the global filters
//   3 – rows of widgets replaced by boxes on a grid
//   4 – the summary boxes became stat widgets, and widgets' value modes calculated fields
export const DASHBOARD_CONFIG_VERSION = 4;

export type DashboardConfig = {
    version?: number;
    // The saved configuration this dashboard was last loaded from or saved as; it names the
    // dashboard's tab. Unset for one that was never saved.
    name?: string;
    widgets: DashboardWidgetConfig[];
    layout: DashboardLayoutConfig;
    // Page-level filters applied on top of every widget's own filters.
    globalFilters: DashboardFilter[];
    // Pre-version-2 only, read once by normalizeDashboardConfig and folded into
    // `globalFilters`. Unset on an old config meant "the built-in defaults".
    baseFilters?: DashboardFilter[];
    // Before version 4 only, read once by normalizeDashboardConfig and turned into stat
    // widgets. Unset on an old config meant "every box, the built-in defaults".
    summary?: LegacySummaryConfig;
    // Formulas the widgets can use as a metric, a dimension or a filter, each written against
    // one source. See dashboardCalculations.ts.
    calculatedFields?: DashboardCalculatedField[];
};

// The calculated fields written against the source a widget queries.
export const calculatedFieldsForSource = (
    calcs: DashboardCalculatedField[] | undefined,
    datasourceId?: string | number,
): DashboardCalculatedField[] => {
    const source = dashboardWidgetSource(datasourceId);
    return (calcs ?? []).filter(c => (c.source ?? DASHBOARD_FILTER_SOURCE_DEFAULT) === source);
};

const DEFAULT_SOURCE = DASHBOARD_FILTER_SOURCE_DEFAULT;
const aggregateOf = (m: DashboardMetric): string => `${m.aggregation}(${m.field})`;

// Add a calculated field unless the source already has one with the same formula, and return
// its id. Ids are fixed (`received`, then `received2`…) rather than random, so normalizing an
// old dashboard twice gives the same dashboard both times.
const ensureCalc = (
    calcs: DashboardCalculatedField[],
    calc: Omit<DashboardCalculatedField, 'id'>,
    preferredId: string,
): string => {
    const source = calc.source ?? DEFAULT_SOURCE;
    const same = calcs.find(
        c =>
            (c.source ?? DEFAULT_SOURCE) === source &&
            c.kind === calc.kind &&
            c.expression === calc.expression,
    );
    if (same) return same.id;
    let id = preferredId;
    for (let n = 2; calcs.some(c => c.id === id); n += 1) id = `${preferredId}${n}`;
    calcs.push({ id, ...calc });
    return id;
};

const calcMetric = (id: string): DashboardMetric => ({
    field: calcFieldOf(id),
    aggregation: CALC_METRIC_AGGREGATION,
});

// Before version 4 a widget could chart a value worked out from its metrics — what was
// received, received less expected, or received as a share of expected — picked from a "Value"
// list. Calculated fields do the same and more, so that list is gone and each such widget now
// charts the calculated field that works its value out. A widget comparing expected with actual
// drew both figures whatever the list said, so it just loses the setting.
type LegacyValueMode = 'raw' | 'received' | 'additional' | 'performance';
const migrateValueMode = (
    widget: DashboardWidgetConfig & { valueMode?: LegacyValueMode },
    calcs: DashboardCalculatedField[],
): DashboardWidgetConfig => {
    const { valueMode, ...rest } = widget;
    const plain = (widget.metrics ?? []).filter(m => !isCalcField(m.field));
    const expected = expectedMetricOf(plain);
    const comparing =
        widget.graphType === 'deliveryComparison' || (widget.comparison === true && expected);
    if (valueMode == null || valueMode === 'raw' || comparing) return rest;
    // "Received" was the metrics named so, or failing that every one that isn't expected.
    const named = plain.filter(m => /received/i.test(m.field));
    const receivedMetrics = named.length ? named : plain.filter(m => !isExpectedField(m.field));
    if (receivedMetrics.length === 0) return rest;
    const received = receivedMetrics.map(aggregateOf).join(' + ');
    const source = dashboardWidgetSource(widget.datasourceId);
    let id: string;
    if (valueMode === 'received') {
        id = ensureCalc(
            calcs,
            {
                name: 'All Impacts',
                kind: 'measure',
                expression: received,
                format: 'number',
                source,
            },
            'received',
        );
    } else if (!expected) {
        // Nothing to compare with: additional was everything received, performance nothing.
        return rest;
    } else if (valueMode === 'additional') {
        id = ensureCalc(
            calcs,
            {
                name: 'Additional Impacts',
                kind: 'measure',
                expression: `${received} - ${aggregateOf(expected)}`,
                format: 'number',
                source,
            },
            'additional',
        );
    } else {
        id = ensureCalc(
            calcs,
            {
                name: '% Impacts',
                kind: 'measure',
                expression: `(${received}) / ${aggregateOf(expected)}`,
                format: 'percent',
                source,
            },
            'performance',
        );
    }
    return { ...rest, metrics: [calcMetric(id)] };
};

// The boxes along the top of the built-in dashboard, as stat widgets on the grid's first row,
// with the calculated fields they read. Shared by the default dashboard and the migration of
// one saved before version 4, which had them above its widgets.
export const SUMMARY_BOX_HEIGHT = 5;
const summaryBoxWidgets = (
    options: {
        source: string;
        fields: Record<SummaryRole, string>;
        boxes: readonly SummaryBox[];
        filters: DashboardFilter[];
        compareTo?: DashboardStatComparison;
    },
    calcs: DashboardCalculatedField[],
    takenIds: Set<string>,
): { widgets: DashboardWidgetConfig[]; items: DashboardGridItem[] } => {
    const { source, fields, boxes, filters, compareTo } = options;
    const datasourceId = source === DEFAULT_SOURCE ? undefined : source;
    const sum = (field: string): DashboardMetric => ({ field, aggregation: 'sum' });
    const received = [fields.inSchedule, fields.outOfSchedule, fields.unbooked]
        .filter(Boolean)
        .map(field => aggregateOf(sum(field)))
        .join(' + ');
    const expected = aggregateOf(sum(fields.expected));
    const calc = (
        preferredId: string,
        name: string,
        expression: string,
        format: 'number' | 'percent' = 'number',
    ) =>
        calcMetric(
            ensureCalc(calcs, { name, kind: 'measure', expression, format, source }, preferredId),
        );
    const allImpacts = () => calc('received', 'All Impacts', received);
    const additional = () => calc('additional', 'Additional Impacts', `${received} - ${expected}`);
    const specs: Record<SummaryBox, Partial<DashboardWidgetConfig>> = {
        campaigns: {
            customTitle: 'Campaigns',
            metrics: [calc('campaigns', 'Campaigns', 'rows()')],
            dimensions: [{ field: fields.campaign }],
            statIcon: 'campaign',
            ...(compareTo ? { compareTo } : {}),
        },
        target: {
            customTitle: 'Target impacts',
            metrics: [sum(fields.expected)],
            statIcon: 'target',
            ...(compareTo ? { compareTo } : {}),
        },
        total: {
            customTitle: 'Total impacts',
            metrics: [allImpacts()],
            statIcon: 'visibility',
            ...(compareTo ? { compareTo } : {}),
        },
        additional: {
            customTitle: 'Additional impacts',
            metrics: [additional()],
            statIcon: 'trendingUp',
            signColors: true,
            ...(compareTo ? { compareTo } : {}),
        },
        pacing: {
            customTitle: 'Pacing to target',
            metrics: [
                calc('performance', '% Impacts', `(${received}) / ${expected}`, 'percent'),
                additional(),
            ],
            statStyle: 'ring',
            signColors: true,
        },
    };
    // Spread across the row, any spare columns going to the boxes at the end.
    const width = Math.floor(DASHBOARD_GRID_COLS / Math.max(boxes.length, 1));
    const spare = DASHBOARD_GRID_COLS - width * boxes.length;
    let x = 0;
    const widgets: DashboardWidgetConfig[] = [];
    const items: DashboardGridItem[] = [];
    boxes.forEach((box, i) => {
        let id = `summary-${box}`;
        for (let n = 2; takenIds.has(id); n += 1) id = `summary-${box}-${n}`;
        takenIds.add(id);
        widgets.push({
            id,
            title: id,
            graphType: 'stat',
            ...(datasourceId != null ? { datasourceId } : {}),
            metrics: [],
            dimensions: [],
            filters,
            sort: [],
            limit: DASHBOARD_MAX_LIMIT,
            statSize: 'medium',
            ...specs[box],
        });
        const w = width + (i >= boxes.length - spare ? 1 : 0);
        items.push({ widgetId: id, x, y: 0, w, h: SUMMARY_BOX_HEIGHT });
        x += w;
    });
    return { widgets, items };
};

const impactMetrics: DashboardMetric[] = [
    { field: 'expected_impacts_in_schedule', aggregation: 'sum' },
    { field: 'impacts_received_in_schedule', aggregation: 'sum' },
    { field: 'impacts_received_out_of_schedule', aggregation: 'sum' },
    { field: 'impacts_received_unbooked', aggregation: 'sum' },
];

const defaultWidget = (
    id: string,
    title: string,
    graphType: DashboardGraphType,
    dimensionField: string,
    metrics: DashboardMetric[] = impactMetrics,
): DashboardWidgetConfig => ({
    id,
    title,
    graphType,
    metrics,
    dimensions: [{ field: dimensionField }],
    filters: [],
    sort: [],
    limit: 100000,
});

export const getDefaultDashboardConfig = (): DashboardConfig => {
    const calculatedFields: DashboardCalculatedField[] = [];
    const boxes = summaryBoxWidgets(
        {
            source: DEFAULT_SOURCE,
            fields: DEFAULT_SUMMARY_FIELDS,
            boxes: SUMMARY_BOXES,
            // The dashboard's global filters are the year to date already.
            filters: [],
            compareTo: LAST_YEAR,
        },
        calculatedFields,
        new Set(),
    );
    // The same formulas the boxes use, so each is listed once.
    const byId = (id: string) => [calcMetric(id)];
    const widgets: DashboardWidgetConfig[] = [
        ...boxes.widgets,
        defaultWidget('impact-delivery', 'Impacts Delivery', 'deliveryComparison', 'display_env'),
        defaultWidget(
            'owners-donut',
            'Top 5 Performing Media Owners (All Impacts)',
            'donut',
            'display_owner',
            byId('received'),
        ),
        defaultWidget(
            'owners-impacts',
            'Top 5 Performing Media Owners (All Impacts)',
            'rankedBar',
            'display_owner',
            byId('received'),
        ),
        defaultWidget(
            'owners-additional',
            'Top 5 Performing Media Owners (Additional Impacts)',
            'rankedBar',
            'display_owner',
            byId('additional'),
        ),
        defaultWidget(
            'owners-perf',
            'Top 5 Performing Media Owners (% Impacts)',
            'rankedBar',
            'display_owner',
            byId('performance'),
        ),
        defaultWidget(
            'envs-perf',
            'Top 5 Performing Environments (% Impacts)',
            'rankedBar',
            'display_env',
            byId('performance'),
        ),
    ];
    const y = SUMMARY_BOX_HEIGHT;
    return {
        version: DASHBOARD_CONFIG_VERSION,
        widgets,
        layout: {
            items: [
                ...boxes.items,
                { widgetId: 'impact-delivery', x: 0, y, w: 8, h: 9 },
                { widgetId: 'owners-donut', x: 8, y, w: 4, h: 9 },
                { widgetId: 'owners-impacts', x: 0, y: y + 9, w: 3, h: 9 },
                { widgetId: 'owners-additional', x: 3, y: y + 9, w: 3, h: 9 },
                { widgetId: 'owners-perf', x: 6, y: y + 9, w: 3, h: 9 },
                { widgetId: 'envs-perf', x: 9, y: y + 9, w: 3, h: 9 },
            ],
        },
        globalFilters: getDashboardBaseFilters().map(f => ({
            ...f,
            source: DEFAULT_SOURCE,
        })),
        calculatedFields,
    };
};

// A dashboard saved before version 4 showed the summary boxes above its widgets — every box,
// on the default report, unless it said otherwise. They become stat widgets on the first row,
// with everything else moved down below them.
const migrateSummary = (
    summary: LegacySummaryConfig | undefined,
    widgets: DashboardWidgetConfig[],
    items: DashboardGridItem[],
    calcs: DashboardCalculatedField[],
): { widgets: DashboardWidgetConfig[]; items: DashboardGridItem[] } => {
    const boxes = Array.isArray(summary?.boxes)
        ? SUMMARY_BOXES.filter(box => summary?.boxes?.includes(box))
        : SUMMARY_BOXES;
    if (boxes.length === 0) return { widgets, items };
    const source = typeof summary?.source === 'string' ? summary.source : DEFAULT_SOURCE;
    // The default report's columns are fixed. A datasource's were detected from its schema
    // as the boxes rendered, which a stored config can't do; its own picks win, and anything
    // it left to detection is assumed to be named as on the default report.
    const fields =
        source === DEFAULT_SOURCE
            ? DEFAULT_SUMMARY_FIELDS
            : { ...DEFAULT_SUMMARY_FIELDS, ...(summary?.fields ?? {}) };
    const built = summaryBoxWidgets(
        {
            source,
            fields,
            boxes,
            // Unset, the boxes counted the year to date, which the global filters do as well.
            filters: Array.isArray(summary?.filters) ? summary.filters : [],
            ...(summary?.comparison !== false ? { compareTo: LAST_YEAR } : {}),
        },
        calcs,
        new Set(widgets.map(w => w.id)),
    );
    return {
        widgets: [...built.widgets, ...widgets],
        items: [
            ...built.items,
            ...items.map(item => ({ ...item, y: item.y + SUMMARY_BOX_HEIGHT })),
        ],
    };
};

// Keep the layout in sync with the widget set: a box for every widget and none for widgets
// that are gone, every box on the grid. A layout stored as rows (before version 3) is turned
// into boxes first, and anything older than version 4 is brought up to date (see above).
export const normalizeDashboardConfig = (config: DashboardConfig | undefined): DashboardConfig => {
    const base = config ?? getDefaultDashboardConfig();
    const version = base.version ?? 1;
    const calculatedFields = Array.isArray(base.calculatedFields)
        ? base.calculatedFields.filter(c => c && typeof c.id === 'string')
        : [];
    const storedWidgets = (Array.isArray(base.widgets) ? base.widgets : []).map(w =>
        migrateValueMode(w, calculatedFields),
    );
    const storedItems = base.layout?.items;
    const legacyRows = base.layout?.rows;
    const storedBoxes = Array.isArray(storedItems)
        ? storedItems
        : Array.isArray(legacyRows)
        ? rowsToGrid(
              legacyRows.map(row => ({
                  items: (row?.items ?? []).filter(
                      (it, i, all) => all.findIndex(x => x.widgetId === it.widgetId) === i,
                  ),
              })),
              id => storedWidgets.find(w => w.id === id)?.graphType,
          )
        : [];
    const { widgets, items: boxes } =
        version < 4
            ? migrateSummary(
                  base.summary && typeof base.summary === 'object' ? base.summary : undefined,
                  storedWidgets,
                  normalizeGrid(storedBoxes, storedWidgets),
                  calculatedFields,
              )
            : { widgets: storedWidgets, items: storedBoxes };
    // A widget placed twice keeps its first box.
    const items = normalizeGrid(boxes, widgets);

    // Global filters predate the source scope, and back then they only ever reached widgets
    // on the default report — a datasource widget dropped them along with the base filters.
    // Pin the scope-less ones there so an existing dashboard keeps filtering exactly as it did,
    // rather than suddenly sending PCA field names to a datasource that lacks them.
    const scoped = (Array.isArray(base.globalFilters) ? base.globalFilters : []).map(
        (f: DashboardFilter) => ({ ...f, source: f.source ?? DEFAULT_SOURCE }),
    );
    // Before version 2 the default-report widgets also got a separate layer of base filters —
    // the config's own list, or the built-in defaults when it had none. They are ordinary
    // global filters now, so fold them in and the dashboard keeps filtering exactly as it did.
    const legacyBase =
        version < 2
            ? (Array.isArray(base.baseFilters) ? base.baseFilters : getDashboardBaseFilters()).map(
                  f => ({ ...f, source: DEFAULT_SOURCE }),
              )
            : [];
    const globalFilters = [...legacyBase, ...scoped];
    const name = typeof base.name === 'string' && base.name.trim() ? base.name.trim() : undefined;
    return {
        version: DASHBOARD_CONFIG_VERSION,
        ...(name ? { name } : {}),
        widgets,
        layout: { items },
        globalFilters,
        ...(calculatedFields.length ? { calculatedFields } : {}),
    };
};

// Structural comparison used to tell a custom dashboard apart from the built-in one.
// Key order is ignored, and an absent key counts as equal to an explicitly undefined one.
const stableEqual = (a: unknown, b: unknown): boolean => {
    if (a === b) return true;
    if (Array.isArray(a) || Array.isArray(b)) {
        if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
        return a.every((item, i) => stableEqual(item, b[i]));
    }
    if (typeof a === 'object' && typeof b === 'object' && a !== null && b !== null) {
        const left = a as Record<string, unknown>;
        const right = b as Record<string, unknown>;
        const keys = new Set(
            [...Object.keys(left), ...Object.keys(right)].filter(
                k => left[k] !== undefined || right[k] !== undefined,
            ),
        );
        return [...keys].every(k => stableEqual(left[k], right[k]));
    }
    return false;
};

// True when the config is indistinguishable from the built-in dashboard. The Custom tab
// stays hidden in that case — it only shows up once something actually differs.
// The same dashboard once normalised: key order and explicitly undefined keys don't count.
export const sameDashboardConfig = (a: DashboardConfig, b: DashboardConfig): boolean =>
    stableEqual(normalizeDashboardConfig(a), normalizeDashboardConfig(b));

export const isDefaultDashboardConfig = (config: DashboardConfig | null | undefined): boolean =>
    config == null || sameDashboardConfig(config, getDefaultDashboardConfig());

// A dashboard the viewer builds with the cog is scratch work: it lives for the visit and is
// gone on reload or on navigating away. Anything meant to last is saved by name (and can then
// be assigned), which is what the Dashboard Setup page manages — so there is nothing to keep
// in localStorage here. Null means "not built one this session", and the Custom tab falls back
// to any assigned dashboards instead.
// Typed rather than a bare `null`: with strictNullChecks off, a null literal makes jotai
// resolve to its read-only atom overload and the setter comes back as `never`.
const NO_SESSION_DASHBOARD = null as DashboardConfig | null;
export const atom_customDashboardConfig = atom<DashboardConfig | null>(NO_SESSION_DASHBOARD);

// Which dashboard the Custom tab is showing: 'local' for the viewer's own, or
// `assigned:<id>` for one an admin assigned to them. Remembered per browser so the choice
// survives a reload; an unknown or no-longer-available key falls back automatically.
const SELECTED_KEY = 'dashboard.selected.v1';

const loadSelectedDashboardKey = (): string | null => {
    try {
        return window.localStorage.getItem(SELECTED_KEY);
    } catch {
        return null;
    }
};

export const persistSelectedDashboardKey = (key: string | null): void => {
    try {
        if (key == null) window.localStorage.removeItem(SELECTED_KEY);
        else window.localStorage.setItem(SELECTED_KEY, key);
    } catch {
        // ignore storage failures (private mode, quota, etc.)
    }
};

export const atom_selectedDashboardKey = atom<string | null>(loadSelectedDashboardKey());

let calcIdSeq = 0;
export const newDashboardCalcId = (): string => {
    calcIdSeq += 1;
    return `c${Date.now().toString(36)}${calcIdSeq}`;
};

let widgetIdSeq = 0;
export const newDashboardWidgetId = (): string => {
    widgetIdSeq += 1;
    return `widget-${widgetIdSeq}-${DASHBOARD_ALL_FIELDS.length}`;
};

export const createDefaultWidget = (id: string): DashboardWidgetConfig => ({
    id,
    title: 'New widget',
    graphType: 'bar',
    metrics: [{ field: 'impacts_received_in_schedule', aggregation: 'sum' }],
    dimensions: [{ field: 'display_env' }],
    filters: [],
    sort: [],
    limit: 100,
});
